import { useEffect, useState, useCallback } from "react";
import { useAuth } from "@/contexts/AuthContext";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { 
  Upload, 
  FileText, 
  Download, 
  Trash2,
  Search,
  FolderOpen,
  BookOpen,
  FileSpreadsheet,
  File,
  Database,
  Sparkles,
  Loader2
} from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CATEGORIES = [
  "Arbres",
  "Arbustes",
  "Plantes",
  "Gazon",
  "Béton",
  "Pierres",
  "Clôtures",
  "Mobilier",
  "Éclairage",
  "Arrosage",
  "Terrassement",
  "Tarifs main d'œuvre",
  "Général"
];

export default function KnowledgeBase() {
  const { getAuthHeaders } = useAuth();
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("general");
  const [deleteId, setDeleteId] = useState(null);
  const [dragActive, setDragActive] = useState(false);
  const [ragStats, setRagStats] = useState(null);
  const [ragSearch, setRagSearch] = useState("");
  const [ragResults, setRagResults] = useState([]);
  const [ragSearching, setRagSearching] = useState(false);

  const fetchDocuments = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/knowledge`, { headers: getAuthHeaders() });
      setDocuments(response.data);
    } catch (error) {
      toast.error("Erreur lors du chargement");
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders]);

  const fetchRagStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/rag/stats`, { headers: getAuthHeaders() });
      setRagStats(response.data);
    } catch (error) {
      console.error("RAG stats error:", error);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchDocuments();
    fetchRagStats();
  }, [fetchDocuments, fetchRagStats]);

  const handleRagSearch = async (e) => {
    e.preventDefault();
    if (!ragSearch.trim()) return;
    
    setRagSearching(true);
    try {
      const response = await axios.post(
        `${API}/rag/search`,
        null,
        { 
          headers: getAuthHeaders(),
          params: { query: ragSearch, top_k: 5 }
        }
      );
      setRagResults(response.data.results || []);
      if (response.data.results?.length === 0) {
        toast.info("Aucun résultat trouvé");
      }
    } catch (error) {
      toast.error("Erreur lors de la recherche");
    } finally {
      setRagSearching(false);
    }
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === "dragenter" || e.type === "dragover") {
      setDragActive(true);
    } else if (e.type === "dragleave") {
      setDragActive(false);
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    const files = Array.from(e.dataTransfer.files).filter(
      f => f.name.endsWith('.xlsx') || f.name.endsWith('.xls') || f.name.endsWith('.pdf') || f.name.endsWith('.csv')
    );
    
    if (files.length > 0) {
      await uploadFiles(files);
    } else {
      toast.error("Seuls les fichiers Excel, PDF et CSV sont acceptés");
    }
  };

  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      await uploadFiles(files);
    }
  };

  const uploadFiles = async (files) => {
    setUploading(true);
    
    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        formData.append('category', selectedCategory);
        
        await axios.post(
          `${API}/knowledge/upload`,
          formData,
          { headers: { ...getAuthHeaders(), 'Content-Type': 'multipart/form-data' } }
        );
        
        toast.success(`${file.name} importé avec succès`);
      } catch (error) {
        toast.error(`Erreur avec ${file.name}: ${error.response?.data?.detail || 'Erreur inconnue'}`);
      }
    }
    
    setUploading(false);
    fetchDocuments();
  };

  const handleDownload = async (doc) => {
    try {
      const response = await axios.get(
        `${API}/knowledge/${doc.id}/download`,
        { 
          headers: getAuthHeaders(),
          responseType: 'blob'
        }
      );
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', doc.filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error("Erreur lors du téléchargement");
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    
    try {
      await axios.delete(`${API}/knowledge/${deleteId}`, { headers: getAuthHeaders() });
      setDocuments(prev => prev.filter(d => d.id !== deleteId));
      toast.success("Document supprimé");
    } catch (error) {
      toast.error("Erreur lors de la suppression");
    } finally {
      setDeleteId(null);
    }
  };

  const filteredDocs = documents.filter(d => 
    d.filename.toLowerCase().includes(search.toLowerCase()) ||
    d.category.toLowerCase().includes(search.toLowerCase())
  );

  const getFileIcon = (fileType) => {
    if (fileType === 'pdf') return <File className="h-5 w-5 text-red-500" />;
    if (['xlsx', 'xls', 'csv'].includes(fileType)) return <FileSpreadsheet className="h-5 w-5 text-green-600" />;
    return <FileText className="h-5 w-5 text-gray-500" />;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-CH', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };

  return (
    <Layout>
      <div className="space-y-6 fade-in">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">
            Base de connaissances
          </h1>
          <p className="text-gray-500 mt-1">
            Gérez vos documents de référence pour les chiffrages
          </p>
        </div>

        {/* RAG Stats & Search */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#1E8B5A]" />
                Recherche avancée
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleRagSearch} className="flex gap-2">
                <Input
                  placeholder="Rechercher des tarifs..."
                  value={ragSearch}
                  onChange={(e) => setRagSearch(e.target.value)}
                  className="flex-1"
                  data-testid="rag-search-input"
                />
                <Button type="submit" disabled={ragSearching} variant="outline">
                  {ragSearching ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </form>
              {ragResults.length > 0 && (
                <div className="mt-4 space-y-2 max-h-48 overflow-y-auto" data-testid="rag-results-list">
                  {ragResults.map((result, idx) => (
                    <div key={idx} className="p-3 bg-green-50 rounded-lg border border-green-100" data-testid={`rag-result-${idx}`}>
                      <div className="flex justify-between items-start gap-2">
                        <span className="text-sm font-medium text-gray-800">{result.description}</span>
                        <span className="text-xs text-green-600 whitespace-nowrap">{((result.score || 0) * 100).toFixed(0)}%</span>
                      </div>
                      <div className="flex justify-between items-center mt-1">
                        <span className="text-xs text-green-700">{result.category} · {result.unit}</span>
                        <span className="text-sm font-semibold text-[#1E8B5A]">{result.unit_price} CHF</span>
                      </div>
                      <p className="text-xs text-gray-400 mt-1 truncate">{result.source_document}</p>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex items-center gap-2">
                <Database className="h-4 w-4 text-[#1E8B5A]" />
                Base vectorielle
              </CardTitle>
            </CardHeader>
            <CardContent>
              {ragStats ? (
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-3 bg-gray-50 rounded-lg" data-testid="rag-stat-vectors">
                    <p className="text-2xl font-bold text-[#1E8B5A]">{ragStats.total_vectors || 0}</p>
                    <p className="text-xs text-gray-500">Références indexées</p>
                  </div>
                  <div className="text-center p-3 bg-gray-50 rounded-lg" data-testid="rag-stat-dimension">
                    <p className="text-2xl font-bold text-[#1E8B5A]">{ragStats.dimension || 0}</p>
                    <p className="text-xs text-gray-500">Dimensions vectorielles</p>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-gray-500">Chargement...</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Upload zone */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Upload className="h-5 w-5 text-[#1E8B5A]" />
              Importer des documents
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="flex gap-4 mb-4">
              <Select value={selectedCategory} onValueChange={setSelectedCategory}>
                <SelectTrigger className="w-64">
                  <SelectValue placeholder="Catégorie" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat.toLowerCase()}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div
              className={`
                border-2 border-dashed rounded-xl p-8 text-center transition-all
                ${dragActive ? 'border-[#1E8B5A] bg-green-50' : 'border-gray-200 hover:border-gray-300'}
                ${uploading ? 'opacity-50 pointer-events-none' : ''}
              `}
              onDragEnter={handleDrag}
              onDragLeave={handleDrag}
              onDragOver={handleDrag}
              onDrop={handleDrop}
            >
              {uploading ? (
                <div className="flex flex-col items-center">
                  <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#1E8B5A] border-t-transparent mb-4" />
                  <p className="text-gray-600">Import en cours...</p>
                </div>
              ) : (
                <>
                  <BookOpen className="h-10 w-10 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600 mb-2">
                    Glissez vos fichiers ici ou{" "}
                    <label className="text-[#1E8B5A] hover:underline cursor-pointer">
                      parcourez
                      <input
                        type="file"
                        className="hidden"
                        accept=".xlsx,.xls,.pdf,.csv"
                        multiple
                        onChange={handleFileChange}
                        data-testid="file-upload"
                      />
                    </label>
                  </p>
                  <p className="text-sm text-gray-400">
                    Formats acceptés : Excel (.xlsx, .xls), PDF, CSV
                  </p>
                </>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Documents list */}
        <Card>
          <CardHeader>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
              <CardTitle>Documents ({documents.length})</CardTitle>
              <div className="relative max-w-xs">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  placeholder="Rechercher..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-9"
                  data-testid="search-docs"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="animate-pulse flex items-center gap-4 p-4 rounded-lg bg-gray-100">
                    <div className="h-10 w-10 rounded-lg bg-gray-200" />
                    <div className="flex-1 space-y-2">
                      <div className="h-4 w-1/3 bg-gray-200 rounded" />
                      <div className="h-3 w-1/4 bg-gray-200 rounded" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredDocs.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="h-12 w-12 text-gray-300 mx-auto mb-4" />
                <h3 className="font-medium text-gray-900 mb-2">
                  {search ? "Aucun résultat" : "Aucun document"}
                </h3>
                <p className="text-sm text-gray-500">
                  {search ? "Essayez une autre recherche" : "Importez vos premiers documents de tarifs"}
                </p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Document</TableHead>
                    <TableHead>Catégorie</TableHead>
                    <TableHead>Éléments</TableHead>
                    <TableHead>Date</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredDocs.map((doc) => (
                    <TableRow key={doc.id}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          {getFileIcon(doc.file_type)}
                          <span className="font-medium truncate max-w-xs">
                            {doc.filename}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="px-2 py-1 rounded-full text-xs bg-gray-100 capitalize">
                          {doc.category}
                        </span>
                      </TableCell>
                      <TableCell>{doc.items_count} éléments</TableCell>
                      <TableCell className="text-gray-500">
                        {formatDate(doc.uploaded_at)}
                      </TableCell>
                      <TableCell>
                        <div className="flex justify-end gap-2">
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleDownload(doc)}
                            className="h-8 w-8"
                            title="Télécharger"
                          >
                            <Download className="h-4 w-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setDeleteId(doc.id)}
                            className="h-8 w-8 text-gray-400 hover:text-red-500"
                            title="Supprimer"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Supprimer ce document ?</AlertDialogTitle>
              <AlertDialogDescription>
                Cette action est irréversible. Les éléments indexés seront supprimés de la base de connaissances.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>Annuler</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-red-500 hover:bg-red-600"
              >
                Supprimer
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </Layout>
  );
}
